import { indexDocuments } from '../utils/documentIndex.js';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { store, dbDocToApp, dbAssignmentToApp, dbContactToApp, dbFlowchartToApp, dbOwnershipToApp, dbDealToApp, dbOfferToApp, dbBuyerToApp, dbPackageToApp, dbClosingToApp, dbTaskToApp } from '../utils/supabaseStore';
import { isSupabaseEnabled } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { authedFetch } from '../lib/authedFetch';
import { waitForAuthReady } from '../lib/authSession';
import { loadPricingFromDB } from '../data/strPricing';
import { loadNmPricingReferencesFromDB } from '../data/nmPricingReference';
import { materializeCommissionPayouts, rematerializeCommissionPayouts } from '../utils/materializeCommissionPayouts';
import { materializeRoyaltyPayouts } from '../utils/materializeRoyaltyPayouts';
import { createDealChildrenAttacher } from '../utils/attachDealChildren';
import { onAnyChange } from '../utils/realtimeDispatch';
import { buildLifecycle, normalizeStage } from '../data/dealStages';
import { AppContext } from './appContextValue';
import { useAuth } from './useAuth';
import { isBradUser } from '../utils/userIdentity';
import { canEditDeal, canViewDeal } from '../utils/dealAccess';
import { physicalMailingComingSoon } from '../utils/physicalMailing';

const CLOSED_STAGES_SET = new Set(['Closed', 'Funded']);

// ── Helpers ──────────────────────────────────────────────────────────────────

// Wraps a store call so it works whether the store method is sync (localStorage)
// or async (Supabase). Returns a Promise either way.
function call(fn, ...args) {
  try {
    const result = fn(...args);
    return result instanceof Promise ? result : Promise.resolve(result);
  } catch (e) {
    return Promise.reject(e);
  }
}

// One rule, stated in src/utils/dealAccess.js so the tracker, this
// provider and the tests read the same lines. It used to be inlined
// here without the unclaimed clause, so the database sent every open
// deal and this threw them away before a manager could claim one.
function canUserAccessDeal(user, deal) {
  return canViewDeal(user, deal);
}

function canUserEditDeal(user, deal) {
  return canEditDeal(user, deal);
}

function filterDealsForUser(user, deals) {
  if (!Array.isArray(deals)) return [];
  return deals.filter((deal) => canUserAccessDeal(user, deal));
}

// A non-admin who creates a deal without naming an agent is its agent.
// An explicit null is not "without naming": it is the tracker saying a
// coordinator raised this and the acquisition is open for somebody to
// claim. Stamping over it made every coordinator the buy-side agent of
// their own deals, and nothing was ever up for grabs.
function stampCreatorAsAgent(user, deal) {
  if (!deal || user?.role === 'admin' || !user?.id) return deal;
  if (deal.buySideAgentId !== undefined || deal.sellSideAgentId) return deal;
  return { ...deal, buySideAgentId: user.id };
}

function hasLifecycleEntries(lifecycle) {
  return !!lifecycle && typeof lifecycle === 'object' && Object.keys(lifecycle).length > 0;
}

function prepareDealForInsert(deal) {
  const stage = normalizeStage(deal?.stage);
  return {
    ...deal,
    stage,
    lifecycle: hasLifecycleEntries(deal?.lifecycle) ? deal.lifecycle : buildLifecycle(stage),
  };
}

function rollbackOptimisticPatch(currentRow, previousRow, patchKeys, optimisticUpdatedAt) {
  if (!currentRow || !previousRow) return previousRow || currentRow || null;
  const reverted = { ...currentRow };
  for (const key of patchKeys || []) {
    if (Object.prototype.hasOwnProperty.call(previousRow, key)) {
      reverted[key] = previousRow[key];
    } else {
      delete reverted[key];
    }
  }
  if (optimisticUpdatedAt && currentRow.updatedAt === optimisticUpdatedAt) {
    reverted.updatedAt = previousRow.updatedAt;
  }
  return reverted;
}

function rollbackOptimisticRowInList(currentRows, id, previousRow, patchKeys, optimisticUpdatedAt) {
  if (!previousRow) return currentRows;
  return (currentRows || []).map((row) => {
    if (row?.id !== id) return row;
    return rollbackOptimisticPatch(row, previousRow, patchKeys, optimisticUpdatedAt);
  });
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AppProvider({ children, deferHeavyHydration = false }) {
  const { user } = useAuth();
  const [assignments,  setAssignments]  = useState([]);
  const [flowcharts,   setFlowcharts]   = useState([]);
  const [documents,    setDocuments]    = useState([]);
  const [contacts,     setContacts]     = useState([]);
  const [ownership,    setOwnership]    = useState([]);
  const [staffUsers,   setStaffUsers]   = useState([]);
  const [deals,        setDeals]        = useState([]);
  // Normalized children of deals (migration-028). Each is an array
  // of all rows; per-deal lookups happen via the dealsWithChildren
  // memo below so consumers can keep reading deal.todos / .activities
  // / .signatureRequests like before.
  const [dealTodos,             setDealTodos]             = useState([]);
  // Rows for the deals somebody has OPENED. The board never draws an
  // activity row — it only asks how long a deal has been quiet — so the
  // 2,563 rows it used to download on every page load are now one
  // timestamp per deal, and the rows arrive when a deal is opened.
  const [dealActivities,        setDealActivities]        = useState([]);
  const [dealActivityStamps,    setDealActivityStamps]    = useState(() => new Map());
  // The stored state of each deal (deal_state_summaries): next action,
  // blockers, health, written by the ten-minute sweep from the same
  // rules the deal page runs. dealId → { summary, computedAt,
  // sourceUpdatedAt }. The board reads it for every card; loaded once
  // after first paint and folded row by row over Realtime.
  const [dealStateSummaries,    setDealStateSummaries]    = useState(() => new Map());
  // Deals whose rows have been fetched, so opening one twice is one
  // fetch and an open deal is not confused with a deal that has none.
  const dealActivitiesLoaded = useRef(new Set());
  // Same story for signature requests: the board needs to know what KIND
  // each request is (an offer, or one of the seller's closing forms) and
  // what state it is in, but not what was offered. The terms are 1,021 kB
  // across the table, so they arrive with the deal.
  const dealSignaturesLoaded = useRef(new Set());
  const [dealSignatureRequests, setDealSignatureRequests] = useState([]);
  const [certifiedMailJobs,     setCertifiedMailJobs]     = useState([]);
  const [offers,       setOffers]       = useState([]);
  const [buyers,       setBuyers]       = useState([]);
  // Investors, and the jointly-owned entities that take title. Admin
  // only — the RLS policy is is_admin(), so for everyone else these
  // simply load empty rather than erroring.
  const [investors,        setInvestors]        = useState([]);
  const [investorEntities, setInvestorEntities] = useState([]);
  const [assetIncome,     setAssetIncome]     = useState([]);
  const [packages,     setPackages]     = useState([]);
  const [closings,     setClosings]     = useState([]);
  const [closingTasks, setClosingTasks] = useState([]);
  const [strPricing,   setStrPricing]   = useState([]);
  const [nmPricingReferences, setNmPricingReferences] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [secondaryLoading, setSecondaryLoading] = useState(true);
  // Lead Center follow-up dates, for the board's "follow up today"
  // marker. Lived on the board as a fetch per mount; one Vercel
  // function round trip — cold-started, the slowest thing left on the
  // path back to the Deal Tracker — for ten rows that rarely change.
  // Loaded here once and kept current below.
  const [followUps, setFollowUps] = useState([]);
  const [allDocumentsLoaded, setAllDocumentsLoaded] = useState(false);
  // Set when a Supabase storage write fails with a network-level error
  // ("Failed to fetch" → usually a paused project, missing bucket, or
  // bad CORS). Shown as a dismissable banner at the top of the layout
  // so users know why their upload didn't persist.
  const [storageError, setStorageError] = useState(null);
  const clearStorageError = useCallback(() => setStorageError(null), []);

  // Track whether initial load is done so real-time updates don't double-load
  const initialised = useRef(false);
  // Phase 2 has run for this session. See hydrateDeferred.
  const deferredHydrated = useRef(false);
  const allDocsLoaded = useRef(false);
  const strPricingRefetchTimer = useRef(null);
  const staffUsersRef = useRef([]);
  const buyersRef = useRef([]);
  const dealsRef = useRef([]);
  // The rep's rate lives on the investor, and materialization runs
  // out-of-band from a callback that must not re-create itself every
  // time the roster loads — same reason the others are refs.
  const investorsRef = useRef([]);
  useEffect(() => { staffUsersRef.current = staffUsers; }, [staffUsers]);
  useEffect(() => { buyersRef.current = buyers; }, [buyers]);
  useEffect(() => { dealsRef.current = deals; }, [deals]);
  useEffect(() => { investorsRef.current = investors; }, [investors]);

  const requireDealEditor = useCallback((dealId, action = 'edit this deal') => {
    const dealForEdit = (dealsRef.current || []).find((d) => d.id === dealId) || null;
    if (!canUserEditDeal(user, dealForEdit)) {
      throw new Error(`You can only ${action} on deals you created or are assigned to.`);
    }
    return dealForEdit;
  }, [user]);

  const assertEditableDealIds = useCallback((dealIds, action = 'edit these deal records') => {
    const ids = Array.from(new Set((dealIds || []).filter(Boolean)));
    for (const dealId of ids) requireDealEditor(dealId, action);
  }, [requireDealEditor]);

  // ── Realtime echo suppression ─────────────────────────────────────────────
  // When the app writes a record, Supabase's postgres_changes stream sends
  // the freshly-saved row back to every subscribed tab — including the tab
  // that just did the write. Without this guard, an update like
  //   A) user types rate A → setDeals(local); updateDeal(db); echo replaces deal with A
  //   B) user types rate B before A's echo arrives → setDeals(local with B)
  //   C) echo for A lands, replaces local state with A → rate B is lost
  // We stamp each write with an expiry + the specific fields touched.
  // The realtime UPDATE handler uses the stamp to merge conservatively:
  //   - if the stamp has no fields (whole-row suppression), drop the echo
  //   - if the stamp lists fields, apply the remote row EXCEPT preserve
  //     the just-written fields from local state (so another user's
  //     concurrent edits to *other* fields still come through).
  // Grace is deliberately generous (1500ms) since Supabase echoes
  // occasionally arrive late behind slower network round-trips.
  const pendingWritesRef = useRef(new Map()); // Map<`${table}:${id}`, { expiresAt, fields: Set<string> | null }>
  const PENDING_WRITE_GRACE_MS = 1500;
  const markLocalWrite = useCallback((table, id, fields) => {
    if (!table || !id) return;
    const key = `${table}:${id}`;
    const existing = pendingWritesRef.current.get(key);
    const expiresAt = Date.now() + PENDING_WRITE_GRACE_MS;
    // If this call is whole-row (no fields given), clear any prior
    // field-level stamp — whole-row suppression is stricter.
    if (!fields) {
      pendingWritesRef.current.set(key, { expiresAt, fields: null });
      return;
    }
    // Merge field sets so back-to-back writes to different fields all
    // get preserved through the grace window.
    const merged = new Set(existing?.fields || []);
    for (const f of fields) merged.add(f);
    pendingWritesRef.current.set(key, {
      expiresAt,
      fields: existing?.fields === null ? null : merged,
    });
  }, []);
  const getLocalWrite = useCallback((table, id) => {
    if (!table || !id) return null;
    const key = `${table}:${id}`;
    const entry = pendingWritesRef.current.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      pendingWritesRef.current.delete(key);
      return null;
    }
    return entry;
  }, []);
  const isEchoOfLocalWrite = useCallback((table, id) => {
    if (!table || !id) return false;
    const key = `${table}:${id}`;
    const entry = pendingWritesRef.current.get(key);
    if (!entry) return false;
    if (Date.now() > entry.expiresAt) {
      pendingWritesRef.current.delete(key);
      return false;
    }
    return true;
  }, []);

  // ── Lazy full-document loader (reusable by DocumentLibrary) ────────────────

  const loadAllDocuments = useCallback(async () => {
    if (allDocsLoaded.current) return null;
    allDocsLoaded.current = true; // Prevent concurrent calls
    const fn = store.getAllDocuments || store.getDocuments;
    const d = await call(fn);
    if (d) {
      setDocuments(d);
      setAllDocumentsLoaded(true);
      return d;
    }
    allDocsLoaded.current = false; // Reset on failure
    return null;
  }, []);

  // Per-deal document hydration. The initial load only carries the 50
  // most recent documents, so a deal whose paperwork is older would
  // look documentless to the checklist evidence checks (signed PSA,
  // closing docs) until the full library loads. Opening a deal pulls
  // its documents by deal_id + attached ids and merges them in.
  const dealDocsLoadedRef = useRef(new Set());
  const ensureDealDocuments = useCallback(async (dealId, attachedIds = []) => {
    if (!dealId || allDocsLoaded.current) return;
    const key = String(dealId);
    if (dealDocsLoadedRef.current.has(key)) return;
    dealDocsLoadedRef.current.add(key);
    const fn = store.getDocumentsForDeal;
    if (typeof fn !== 'function') return;
    try {
      const rows = await fn(dealId, attachedIds);
      if (!Array.isArray(rows) || !rows.length) return;
      setDocuments((prev) => {
        const seen = new Set((prev || []).map((doc) => doc.id));
        const fresh = rows.filter((doc) => doc?.id && !seen.has(doc.id));
        return fresh.length ? [...(prev || []), ...fresh] : prev;
      });
    } catch (err) {
      console.warn('[AppContext] ensureDealDocuments failed:', err?.message);
      dealDocsLoadedRef.current.delete(key);
    }
  }, []);

  // Per-deal activity hydration, the same shape as ensureDealDocuments
  // above and for the same reason: the board does not draw an activity
  // row, so downloading every one of them on page load was 963 kB
  // (2,563 rows on 2026-10-21) for data nothing on screen could show.
  // The board carries one timestamp per deal for its staleness warning;
  // the rows arrive when a deal is opened.
  //
  // Marked loaded BEFORE the fetch so a deal opened twice in quick
  // succession is one request, and unmarked on failure so the next open
  // tries again rather than leaving the timeline permanently empty.
  const ensureDealActivities = useCallback(async (dealId) => {
    if (!dealId) return;
    const key = String(dealId);
    if (dealActivitiesLoaded.current.has(key)) return;
    const fn = store.getDealActivitiesForDeal;
    if (typeof fn !== 'function') return;
    dealActivitiesLoaded.current.add(key);
    try {
      const rows = await fn(dealId);
      setDealActivities((prev) => {
        const others = (prev || []).filter((row) => String(row?.dealId) !== key);
        return [...rows, ...others];
      });
    } catch (err) {
      console.warn('[AppContext] ensureDealActivities failed:', err?.message);
      dealActivitiesLoaded.current.delete(key);
    }
  }, []);

  // The offer terms for one deal, fetched when it opens. Same shape and
  // same reasons as ensureDealActivities above.
  const ensureDealSignatureRequests = useCallback(async (dealId) => {
    if (!dealId) return;
    const key = String(dealId);
    if (dealSignaturesLoaded.current.has(key)) return;
    const fn = store.getDealSignatureRequestsForDeal;
    if (typeof fn !== 'function') return;
    dealSignaturesLoaded.current.add(key);
    try {
      const rows = await fn(dealId);
      setDealSignatureRequests((prev) => {
        const others = (prev || []).filter((row) => String(row?.dealId) !== key);
        return [...rows, ...others];
      });
    } catch (err) {
      console.warn('[AppContext] ensureDealSignatureRequests failed:', err?.message);
      dealSignaturesLoaded.current.delete(key);
    }
  }, []);

  // Read inside loadAll rather than closed over, so that a route change
  // flipping this prop cannot rebuild loadAll and re-run the bootstrap
  // through the effect that depends on it. What the flip IS still good
  // for is handled by its own effect further down.
  const deferRef = useRef(deferHeavyHydration);
  useEffect(() => { deferRef.current = deferHeavyHydration; }, [deferHeavyHydration]);

  /**
   * The owners with a follow-up date. Ten of a few thousand today; the
   * route caps at 500 soonest. A failure leaves the list as it was —
   * the marker is a courtesy, and a board with no markers is the board
   * exactly as it was before the feature.
   */
  const loadFollowUps = useCallback(async () => {
    try {
      const res = await authedFetch('/api/crm/follow-ups');
      if (!res.ok) return;
      const body = await res.json();
      if (Array.isArray(body?.followUps)) setFollowUps(body.followUps);
    } catch (err) {
      console.warn('[AppContext] follow-ups load failed:', err?.message);
    }
  }, []);

  // ── Initial data load ──────────────────────────────────────────────────────

  /**
   * Phase 2: everything the deeper pages read — Owner Dashboard,
   * Document Library, the maps, the reports.
   *
   * Once per session, behind a latch. It is fifteen queries across
   * every one of those tables, and the realtime subscriptions below
   * keep each slice current afterwards, so a second run can only
   * re-download what the app already holds. The latch is what makes
   * the route-change hydration below safe to call freely.
   *
   * `force` is for the explicit Refresh, which is a person saying they
   * do not trust what is on screen.
   */
  const hydrateDeferred = useCallback(({ force = false } = {}) => {
    if (force) deferredHydrated.current = false;
    if (deferredHydrated.current) return;
    deferredHydrated.current = true;
    // Say it is loading again before it is: a consumer that reads
    // secondaryLoading would otherwise see `false` left over from a
    // deferred first paint and treat empty arrays as "no rows".
    setSecondaryLoading(true);
    const runDeferred = () => {
      const deferredPromises = [
        call(store.getAssignments).then(d => { if (d) setAssignments(d); }),
        call(store.getFlowcharts).then(d => { if (d) setFlowcharts(d); }),
        call(store.getDocuments, 50).then(d => { if (d) setDocuments(d); }),
        call(store.getOwnership).then(d => { if (d) setOwnership(d); }),
        call(store.getOffers).then(d => { if (d) setOffers(d); }),
        call(store.getPackages).then(d => { if (d) setPackages(d); }),
        call(store.getClosings).then(d => { if (d) setClosings(d); }),
        call(store.getClosingTasks).then(d => { if (d) setClosingTasks(d); }),
        (store.getSTRPricingData ? call(store.getSTRPricingData) : Promise.resolve(null))
          .then(d => { if (d) { setStrPricing(d); loadPricingFromDB(d); } }),
        (store.getNmSTRPricingReferences ? call(store.getNmSTRPricingReferences) : Promise.resolve(null))
          .then(d => { if (d) { setNmPricingReferences(d); loadNmPricingReferencesFromDB(d); } }),
        (store.getNotifications ? call(store.getNotifications) : Promise.resolve(null))
          .then(d => { if (d) setNotifications(d); }),
        // Normalized deal children (migration-028) — load all rows
        // once so the merge memo can populate deal.todos / .activities
        // / .signatureRequests for every deal the user opens.
        (store.getDealTodos ? call(store.getDealTodos) : Promise.resolve(null))
          .then(d => { if (d) setDealTodos(d); }),
        (store.getDealActivityStamps ? call(store.getDealActivityStamps) : Promise.resolve(null))
          .then(d => { if (d) setDealActivityStamps(new Map(d.map((row) => [row.dealId, row.lastAt]))); }),
        (store.getDealStateSummaries ? call(store.getDealStateSummaries) : Promise.resolve(null))
          .then(d => { if (d) setDealStateSummaries(new Map(d.filter((row) => row.dealId && row.summary).map((row) => [row.dealId, row]))); }),
        (store.getDealSignatureRequests ? call(store.getDealSignatureRequests) : Promise.resolve(null))
          .then(d => { if (d) setDealSignatureRequests(d); }),
        (store.getCertifiedMailJobs ? call(store.getCertifiedMailJobs) : Promise.resolve(null))
          .then(d => { if (d) setCertifiedMailJobs(d); }),
      ];
      Promise.allSettled(deferredPromises).then(() => {
        setSecondaryLoading(false);
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runDeferred);
    } else {
      setTimeout(runDeferred, 0);
    }
  }, []);

  const loadAll = useCallback(async () => {
    // Set when phase 1 came back empty and a retry is already queued.
    // `return` inside the try still runs the finally below, so without
    // this the retry path hydrates phase 2 twice — ~14 duplicate
    // queries on exactly the cold-start where things were already slow.
    let retryQueued = false;
    try {
      // Phase 1: minimal data the Deal Tracker needs to paint.
      // Assignments / flowcharts / ownership_records / documents /
      // offers / packages / closings / strPricing all moved to
      // Phase 2 — the kanban only actually needs deals (the rows),
      // contacts (owner names), staffUsers (agent labels), and
      // buyers (disposition chip). Margins + NRA totals come off
      // deal.interestEstimates which rides the deal row itself.
      const corePromises = [
        call(store.getContacts).then(d => { if (d) setContacts(d); return d; }),
        // Live deals only: the board paints on these, and the Passed
        // half (312 kB, one line per card, excluded from every KPI)
        // follows right after so it is not on the critical path. It
        // still always loads — see the append below, which runs whether
        // or not phase 2 does.
        call(store.getDeals, { liveOnly: true }).then(d => {
          if (d) setDeals(filterDealsForUser(user, d));
          return d;
        }),
        call(store.getUsers).then(d => { if (d) setStaffUsers(d); return d; }),
        call(store.getBuyers).then(d => { if (d) setBuyers(d); return d; }),
        call(store.getInvestors).then(d => { if (d) setInvestors(d); return d; }),
        call(store.getInvestorEntities).then(d => { if (d) setInvestorEntities(d); return d; }),
        call(store.getAssetIncome).then(d => { if (d) setAssetIncome(d); return d; }),
      ];

      const results = await Promise.allSettled(corePromises);

      // If ALL fetches returned empty/failed, it's likely an auth issue — retry after delay
      const anyData = results.some(r => r.status === 'fulfilled' && r.value?.length > 0);
      if (!anyData && !initialised.current) {
        console.warn('[AppContext] No data loaded — retrying in 2s');
        retryQueued = true;
        setTimeout(() => loadAll(), 2000);
        return;
      }
    } catch (err) {
      console.error('[AppContext] loadAll error:', err);
    } finally {
      setLoading(false);
      initialised.current = true;

      // The Passed half, appended once the board has what it needs to
      // paint. Deliberately outside the phase-2 block below: a route
      // that skips heavy hydration still gets its Passed deals, because
      // the deals array is shared with every other page and a missing
      // half would read as deals that no longer exist.
      // Beside the Passed append and for the same reason: every route
      // gets it, phase 2 or not, and it is off the critical path.
      if (!retryQueued) void loadFollowUps();

      if (!retryQueued && store.getPassedDeals) {
        call(store.getPassedDeals).then((passed) => {
          if (!Array.isArray(passed) || passed.length === 0) return;
          const visible = filterDealsForUser(user, passed);
          setDeals((prev) => {
            const known = new Set((prev || []).map((deal) => deal.id));
            const fresh = visible.filter((deal) => deal?.id && !known.has(deal.id));
            return fresh.length ? [...(prev || []), ...fresh] : prev;
          });
        }).catch((err) => {
          console.warn('[AppContext] passed deals load failed:', err?.message);
        });
      }

      if (retryQueued) {
        // The queued retry owns phase 2.
      } else if (deferRef.current) {
        setSecondaryLoading(false);
      } else {
        hydrateDeferred();
      }
    }
  }, [user, hydrateDeferred, loadFollowUps]);


  useEffect(() => {
    if (!isSupabaseEnabled) {
      store.seedDefaults?.();
      loadAll();
      return;
    }

    // Wait for Supabase auth session to be ready before loading data.
    let cancelled = false;
    async function waitAndLoad() {
      await waitForAuthReady(3000);
      if (!cancelled) loadAll();
    }
    waitAndLoad();
    return () => { cancelled = true; };
  }, [loadAll]);

  /**
   * Phase 2, when a route that needs it is reached.
   *
   * THE BUG THIS REPLACES
   *
   * deferHeavyHydration is computed from location.pathname at the
   * provider:
   *
   *   <AppProvider deferHeavyHydration={isMapHeavyPath(location.pathname)}>
   *
   * so it FLIPS every time somebody moves between the map and anything
   * else. It was in loadAll's dependency array, and the bootstrap
   * effect depends on loadAll, so each flip rebuilt loadAll, re-ran the
   * effect, and re-ran the ENTIRE bootstrap: contacts, every live deal
   * with its estimator JSON, the Passed half, staff, buyers, investors,
   * investor entities, asset income, and then all fifteen phase-2
   * queries again. Deal Tracker to the map and back was two of those.
   *
   * Nothing in it was new. The realtime subscriptions below keep every
   * one of those slices current, so the refetch could only ever return
   * what the app already had.
   *
   * WHY NOT SIMPLY DROP THE DEPENDENCY
   *
   * Because the flip was doing one necessary job by accident. Opening
   * the app ON the map skips phase 2 on purpose; the deeper pages need
   * it, and without the reload they would never get it. So the reload
   * is not removed, it is narrowed to the part that was load-bearing:
   * phase 2 runs when a route that wants it is first reached, once.
   */
  useEffect(() => {
    if (deferHeavyHydration) return;
    // The bootstrap has not finished; its own finally will hydrate.
    if (!initialised.current) return;
    hydrateDeferred();
  }, [deferHeavyHydration, hydrateDeferred]);

  // ── Real-time subscriptions (Supabase only) ────────────────────────────────

  useEffect(() => {
    if (!isSupabaseEnabled || !supabase) return;

    // Subscribe to every table we care about — on any change, reload that slice
    const channels = [
      supabase.channel('rt-assignments')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'assignments' }, onAnyChange({
          INSERT: (payload) => {
            const a = dbAssignmentToApp(payload.new);
            setAssignments(prev => prev.some(x => x.id === a.id) ? prev : [a, ...prev]);
          },
          UPDATE: (payload) => {
            const a = dbAssignmentToApp(payload.new);
            if (isEchoOfLocalWrite('assignments', a.id)) return;
            setAssignments(prev => prev.map(x => x.id === a.id ? a : x));
          },
          DELETE: (payload) => setAssignments(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-flowcharts')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'flowcharts' }, onAnyChange({
          INSERT: (payload) => {
            const f = dbFlowchartToApp(payload.new);
            setFlowcharts(prev => prev.some(x => x.id === f.id) ? prev : [f, ...prev]);
          },
          UPDATE: (payload) => {
            const f = dbFlowchartToApp(payload.new);
            if (isEchoOfLocalWrite('flowcharts', f.id)) return;
            setFlowcharts(prev => prev.map(x => x.id === f.id ? f : x));
          },
          DELETE: (payload) => setFlowcharts(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-documents')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'documents' }, onAnyChange({
          INSERT: (payload) => {
            const newDoc = dbDocToApp(payload.new);
            setDocuments(prev => prev.some(d => d.id === newDoc.id) ? prev : [newDoc, ...prev]);
          },
          UPDATE: (payload) => {
            const updated = dbDocToApp(payload.new);
            if (isEchoOfLocalWrite('documents', updated.id)) return;
            setDocuments(prev => prev.map(d => d.id === updated.id ? updated : d));
          },
          DELETE: (payload) => {
            const oldId = payload.old.id;
            setDocuments(prev => prev.filter(d => d.id !== oldId));
          },
        }))
        .subscribe(),

      supabase.channel('rt-contacts')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'contacts' }, onAnyChange({
          INSERT: (payload) => {
            const c = dbContactToApp(payload.new);
            setContacts(prev => prev.some(x => x.id === c.id) ? prev : [c, ...prev]);
          },
          UPDATE: (payload) => {
            const c = dbContactToApp(payload.new);
            if (isEchoOfLocalWrite('contacts', c.id)) return;
            setContacts(prev => prev.map(x => x.id === c.id ? c : x));
          },
          DELETE: (payload) => setContacts(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-ownership')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'ownership_records' }, onAnyChange({
          INSERT: (payload) => {
            const o = dbOwnershipToApp(payload.new);
            setOwnership(prev => prev.some(x => x.id === o.id) ? prev : [o, ...prev]);
          },
          UPDATE: (payload) => {
            const o = dbOwnershipToApp(payload.new);
            if (isEchoOfLocalWrite('ownership_records', o.id)) return;
            setOwnership(prev => prev.map(x => x.id === o.id ? o : x));
          },
          DELETE: (payload) => setOwnership(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      // The follow-up date is set in the Lead Center, on crm_owners. One
      // event per changed row, folded into the small list in place — no
      // refetch — so a date set on a lead shows on its deal card at once
      // and the Vercel function is never called again for the session.
      // Only the fields the marker reads; a row whose date was cleared
      // drops out. RLS scopes the channel to the caller's tenant.
      // The sweep rewrites a deal's summary only when it changed, so each
      // event here is a real change of state: folded into the map in
      // place, and the card re-judges itself from it. RLS scopes the
      // channel to the deals the caller can read.
      supabase.channel('rt-deal-state')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_state_summaries' }, (payload) => {
          const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
          const dealId = row?.deal_id;
          if (!dealId) return;
          setDealStateSummaries((prev) => {
            const next = new Map(prev);
            if (payload.eventType === 'DELETE') {
              if (!next.delete(dealId)) return prev;
              return next;
            }
            const entry = store.dbDealStateSummaryToApp ? store.dbDealStateSummaryToApp(row) : null;
            if (!entry?.summary) return prev;
            const current = prev.get(dealId);
            if (current && current.fingerprint === entry.fingerprint && current.sourceUpdatedAt === entry.sourceUpdatedAt) return prev;
            next.set(dealId, entry);
            return next;
          });
        })
        .subscribe(),

      supabase.channel('rt-crm-follow-ups')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_owners' }, (payload) => {
          const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
          const ownerId = row?.id;
          if (!ownerId) return;
          const next = payload.eventType !== 'DELETE' && row?.next_follow_up
            ? {
              ownerId,
              contactId: row.contact_id || null,
              dealId: row.deal_id || null,
              nextFollowUp: String(row.next_follow_up).slice(0, 10),
            }
            : null;
          setFollowUps((prev) => {
            const rest = prev.filter((f) => f.ownerId !== ownerId);
            if (!next) return rest.length === prev.length ? prev : rest;
            const current = prev.find((f) => f.ownerId === ownerId);
            if (current && current.contactId === next.contactId && current.dealId === next.dealId
              && current.nextFollowUp === next.nextFollowUp) return prev;
            return [...rest, next];
          });
        })
        .subscribe(),

      supabase.channel('rt-deals')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deals' }, onAnyChange({
          INSERT: (payload) => {
            const d = dbDealToApp(payload.new);
            if (!canUserAccessDeal(user, d)) return;
            setDeals(prev => prev.some(x => x.id === d.id) ? prev : [d, ...prev]);
          },
          UPDATE: (payload) => {
            const d = dbDealToApp(payload.new);
            if (!canUserAccessDeal(user, d)) {
              setDeals(prev => prev.filter(x => x.id !== d.id));
              return;
            }
            const pending = getLocalWrite('deals', d.id);
            if (pending && pending.fields === null) {
              // Whole-row suppression (older-style mark) — drop the echo.
              return;
            }
            if (pending && pending.fields) {
              // Field-level: apply the remote row but preserve whichever
              // fields we just wrote locally — another user's concurrent
              // edits to other fields still come through. This avoids
              // clobbering local edits when an unrelated realtime patch
              // lands during the grace window.
              setDeals(prev => prev.map(x => {
                if (x.id !== d.id) return x;
                const merged = { ...x, ...d };
                for (const f of pending.fields) {
                  if (Object.prototype.hasOwnProperty.call(x, f)) merged[f] = x[f];
                }
                return merged;
              }));
              return;
            }
            // Merge, don't replace: dbDealToApp only maps columns the
            // payload actually carries, so a TOAST-trimmed realtime row
            // (large text columns absent when the update didn't touch
            // them) must not drop fields the app already holds.
            setDeals(prev => prev.map(x => x.id === d.id ? { ...x, ...d } : x));
          },
          DELETE: (payload) => setDeals(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      // ── Normalized deal children (migration-028) ────────────────────
      // Each table publishes its own postgres_changes feed; the merge
      // memo below recomposes deal.todos / .activities /
      // .signatureRequests for any consumer that reads them.
      supabase.channel('rt-deal-todos')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_todos' }, onAnyChange({
          INSERT: (payload) => {
            const t = store.dbTodoToApp ? store.dbTodoToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setDealTodos(prev => prev.some(x => x.id === t.id) ? prev : [t, ...prev]);
          },
          UPDATE: (payload) => {
            const t = store.dbTodoToApp ? store.dbTodoToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setDealTodos(prev => prev.map(x => x.id === t.id ? t : x));
          },
          DELETE: (payload) => setDealTodos(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-deal-activities')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_activities' }, onAnyChange({
          INSERT: (payload) => {
            const a = store.dbActivityToApp ? store.dbActivityToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            // The stamp moves for every deal, open or not — it is what
            // the board's staleness warning reads. The row is kept only
            // for a deal whose rows are loaded; adding it otherwise
            // would make a deal with one row look fully loaded.
            setDealActivityStamps(prev => {
              const at = a.createdAt || payload.new?.created_at || null;
              if (!a.dealId || !at || (prev.get(a.dealId) || '') >= at) return prev;
              const next = new Map(prev);
              next.set(a.dealId, at);
              return next;
            });
            if (!dealActivitiesLoaded.current.has(a.dealId)) return;
            setDealActivities(prev => prev.some(x => x.id === a.id) ? prev : [a, ...prev]);
          },
          UPDATE: (payload) => {
            const a = store.dbActivityToApp ? store.dbActivityToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setDealActivities(prev => prev.map(x => x.id === a.id ? a : x));
          },
          DELETE: (payload) => setDealActivities(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-deal-signature-requests')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_signature_requests' }, onAnyChange({
          INSERT: (payload) => {
            const s = store.dbSignatureRequestToApp ? store.dbSignatureRequestToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setDealSignatureRequests(prev => prev.some(x => x.id === s.id) ? prev : [s, ...prev]);
          },
          UPDATE: (payload) => {
            const s = store.dbSignatureRequestToApp ? store.dbSignatureRequestToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setDealSignatureRequests(prev => prev.map(x => x.id === s.id ? s : x));
          },
          DELETE: (payload) => setDealSignatureRequests(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-certified-mail-jobs')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'certified_mail_jobs' }, onAnyChange({
          INSERT: (payload) => {
            const m = store.dbCertifiedMailJobToApp ? store.dbCertifiedMailJobToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setCertifiedMailJobs(prev => prev.some(x => x.id === m.id) ? prev : [m, ...prev]);
          },
          UPDATE: (payload) => {
            const m = store.dbCertifiedMailJobToApp ? store.dbCertifiedMailJobToApp(payload.new) : { id: payload.new.id, dealId: payload.new.deal_id };
            setCertifiedMailJobs(prev => prev.map(x => x.id === m.id ? { ...x, ...m, documents: x.documents || [], events: x.events || [] } : x));
          },
          DELETE: (payload) => setCertifiedMailJobs(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-offers')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'offers' }, onAnyChange({
          INSERT: (payload) => { const o = dbOfferToApp(payload.new); setOffers(prev => prev.some(x => x.id === o.id) ? prev : [o, ...prev]); },
          UPDATE: (payload) => { const o = dbOfferToApp(payload.new); setOffers(prev => prev.map(x => x.id === o.id ? o : x)); },
          DELETE: (payload) => setOffers(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      // Notifications channel — INSERT-only is enough since updates
      // (read_at flips) flow back through markNotificationRead's
      // optimistic state and the user's own action triggers it.
      // Filter server-side by user_id so other users' notifications
      // don't even hit our channel.
      supabase.channel('rt-notifications')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, onAnyChange({
          INSERT: (payload) => {
            const r = payload.new || {};
            const n = {
              id: r.id, kind: r.kind, title: r.title,
              message: r.message || '', linkPath: r.link_path || null,
              meta: r.meta || {}, readAt: r.read_at, createdAt: r.created_at,
            };
            setNotifications((prev) => prev.some((x) => x.id === n.id) ? prev : [n, ...prev]);
          },
        }))
        .subscribe(),

      supabase.channel('rt-buyers')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_profiles' }, onAnyChange({
          INSERT: (payload) => { const b = dbBuyerToApp(payload.new); setBuyers(prev => prev.some(x => x.id === b.id) ? prev : [b, ...prev]); },
          UPDATE: (payload) => { const b = dbBuyerToApp(payload.new); setBuyers(prev => prev.map(x => x.id === b.id ? b : x)); },
          DELETE: (payload) => setBuyers(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-packages')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_packages' }, onAnyChange({
          INSERT: (payload) => { const p = dbPackageToApp(payload.new); setPackages(prev => prev.some(x => x.id === p.id) ? prev : [p, ...prev]); },
          UPDATE: (payload) => { const p = dbPackageToApp(payload.new); setPackages(prev => prev.map(x => x.id === p.id ? p : x)); },
          DELETE: (payload) => setPackages(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-closings')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'closings' }, onAnyChange({
          INSERT: (payload) => { const c = dbClosingToApp(payload.new); setClosings(prev => prev.some(x => x.id === c.id) ? prev : [c, ...prev]); },
          UPDATE: (payload) => { const c = dbClosingToApp(payload.new); setClosings(prev => prev.map(x => x.id === c.id ? c : x)); },
          DELETE: (payload) => setClosings(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      supabase.channel('rt-closing-tasks')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'closing_tasks' }, onAnyChange({
          INSERT: (payload) => { const t = dbTaskToApp(payload.new); setClosingTasks(prev => prev.some(x => x.id === t.id) ? prev : [t, ...prev]); },
          UPDATE: (payload) => { const t = dbTaskToApp(payload.new); setClosingTasks(prev => prev.map(x => x.id === t.id ? t : x)); },
          DELETE: (payload) => setClosingTasks(prev => prev.filter(x => x.id !== payload.old.id)),
        }))
        .subscribe(),

      // Debounce the pricing refetch. Every update to str_pricing used
      // to trigger a fresh `select *` against the whole table — if
      // anyone batch-imports or edits rows in quick succession this
      // amplifies into N full-table fetches. 1s coalesce keeps the
      // map fresh without the storm.
      supabase.channel('rt-str-pricing')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'str_pricing' }, () => {
          if (strPricingRefetchTimer.current) clearTimeout(strPricingRefetchTimer.current);
          strPricingRefetchTimer.current = setTimeout(() => {
            if (!store.getSTRPricingData) return;
            call(store.getSTRPricingData).then(rows => {
              if (rows) { setStrPricing(rows); loadPricingFromDB(rows); }
            });
          }, 1000);
        })
        .subscribe(),
    ];

    return () => {
      channels.forEach(ch => supabase.removeChannel(ch));
      if (strPricingRefetchTimer.current) clearTimeout(strPricingRefetchTimer.current);
    };
  }, [user, getLocalWrite, isEchoOfLocalWrite]);

  // ── Manual refresh (keep for pull-to-refresh scenarios) ───────────────────

  // An explicit Refresh is somebody saying they do not trust what is on
  // screen, so it forces phase 2 past its latch as well. Everything
  // else goes through the latch and gets it at most once.
  const refresh = useCallback(() => {
    deferredHydrated.current = false;
    return loadAll();
  }, [loadAll]);

  // ── ASSIGNMENTS ────────────────────────────────────────────────────────────

  // ── FLOWCHARTS ─────────────────────────────────────────────────────────────

  // Post-migration-019 flowchart creation. Writes a flowchart keyed on
  // (dealId, interestEstimateId) with no assignment coupling. Caller
  // supplies the ids it wants — we don't generate them here so the
  // caller can stamp the same id on related docs/ownership.
  const createFlowchart = useCallback(async (flowchart) => {
    const row = {
      id: flowchart.id,
      dealId: flowchart.dealId || null,
      interestEstimateId: flowchart.interestEstimateId || null,
      title: flowchart.title || 'Title Chain',
      nodes: flowchart.nodes || [],
      edges: flowchart.edges || [],
      createdAt: flowchart.createdAt || new Date().toISOString(),
    };
    setFlowcharts((prev) => [row, ...prev]);
    try {
      await call(store.addFlowchart, row);
    } catch (err) {
      console.error('[AppContext] createFlowchart failed:', err);
      setFlowcharts((prev) => prev.filter((f) => f.id !== row.id));
      throw err;
    }
    return row;
  }, []);

  const updateFlowchart = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    setFlowcharts(p => {
      prev = p;
      prevRow = p.find((f) => f.id === id) || null;
      return p.map(f => f.id === id ? { ...f, ...updates } : f);
    });
    markLocalWrite('flowcharts', id);
    try {
      await call(store.updateFlowchart, id, updates);
    } catch (err) {
      console.error('[AppContext] updateFlowchart failed:', err);
      if (prevRow) setFlowcharts((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setFlowcharts(prev);
      throw err;
    }
  }, [markLocalWrite]);

  const deleteFlowchart = useCallback(async (id) => {
    let prev;
    setFlowcharts(p => { prev = p; return p.filter(f => f.id !== id); });
    try {
      await call(store.deleteFlowchart, id);
    } catch (err) {
      console.error('[AppContext] deleteFlowchart failed:', err);
      if (prev) setFlowcharts(prev);
      throw err;
    }
  }, []);

  // ── DOCUMENTS ──────────────────────────────────────────────────────────────

  const addDocument = useCallback(async (doc) => {
    if (doc?.dealId) requireDealEditor(doc.dealId, 'add documents');
    // Add _searchText only if not already present (supabaseStore's dbDocToApp adds it)
    const enriched = doc._searchText ? doc : {
      ...doc,
      _searchText: [
        doc.name, doc.docType, doc.grantor, doc.grantee,
        doc.instrumentNo, doc.legalDescription, doc.volume, doc.instrumentTitle,
      ].filter(Boolean).join(' ').toLowerCase(),
    };
    setDocuments(prev => [enriched, ...prev]);
    try {
      const saved = await call(store.addDocument, doc);
      // The upload succeeded — swap the optimistic row for a
      // storage-backed one and drop the base64 payload. Keeping the
      // multi-MB dataUrl string in React state made every consumer of
      // `documents` drag for the rest of the session (the "whole app
      // lags after uploading a PSA" bug). On failure the heavy row
      // stays so the doc remains viewable this session.
      const storagePath = saved?.storagePath || doc.storagePath || null;
      if (storagePath) {
        setDocuments(prev => prev.map((d) => {
          if (d.id !== doc.id) return d;
          const light = { ...d, storagePath };
          delete light.dataUrl;
          delete light.fileDataUrl;
          delete light.file;
          return light;
        }));
      }
      return { ok: true };
    } catch (err) {
      console.error('[AppContext] addDocument failed:', err);
      // Keep the optimistic doc in memory so the user can still reference
      // it during this session (the in-memory dataUrl works for viewing)
      // and surface a banner with the actual error message. Return
      // { ok: false, reason } so callers that need strict semantics can
      // roll back their own optimistic state. We no longer try to guess
      // "bucket missing vs CORS vs offline" from the message text — the
      // exact cause is visible in the console log from uploadDocumentFile
      // (HTTP status, path, size) and in the browser's Network tab.
      const msg = err?.message || 'Unknown error';
      const isTransportFailure = /Failed to fetch/i.test(msg);
      if (isTransportFailure) {
        setStorageError({
          title: 'Document upload failed',
          message: `Upload did not reach Supabase (${msg}). The document is available for this session but is not saved. Check the browser Network tab for the storage request — if it shows net::ERR_BLOCKED_BY_CLIENT, disable ad/privacy extensions for this site. Otherwise, check your connection.`,
        });
        return { ok: false, reason: 'transport-failure', error: err };
      }
      // Non-transport failures (HTTP error responses from storage, DB
      // insert errors, etc.) bubble up so the caller can show them —
      // but still show the user a clear banner with the real message.
      setStorageError({
        title: 'Document upload failed',
        message: msg,
      });
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      throw err;
    }
  }, [requireDealEditor]);

  const updateDocument = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    const currentDoc = documents.find((d) => d.id === id) || null;
    assertEditableDealIds([currentDoc?.dealId, updates?.dealId], 'edit documents');
    setDocuments(p => {
      prev = p;
      prevRow = p.find((d) => d.id === id) || null;
      return p.map(d => d.id === id ? { ...d, ...updates } : d);
    });
    markLocalWrite('documents', id);
    try {
      await call(store.updateDocument, id, updates);
    } catch (err) {
      console.error('[AppContext] updateDocument failed:', err);
      if (prevRow) setDocuments((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setDocuments(prev);
      throw err;
    }
  }, [assertEditableDealIds, documents, markLocalWrite]);

  const deleteDocument = useCallback(async (id) => {
    const currentDoc = documents.find((d) => d.id === id) || null;
    if (currentDoc?.dealId) requireDealEditor(currentDoc.dealId, 'delete documents');
    let prev;
    setDocuments(p => { prev = p; return p.filter(d => d.id !== id); });
    try {
      await call(store.deleteDocument, id);
    } catch (err) {
      console.error('[AppContext] deleteDocument failed:', err);
      if (prev) setDocuments(prev);
      throw err;
    }
  }, [documents, requireDealEditor]);

  // ── NOTIFICATIONS (in-app inbox) ───────────────────────────────────────────

  const markNotificationRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
    try { await call(store.markNotificationRead, id); }
    catch (err) { console.warn('[AppContext] markNotificationRead failed:', err?.message); }
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    try { await call(store.markAllNotificationsRead); }
    catch (err) { console.warn('[AppContext] markAllNotificationsRead failed:', err?.message); }
  }, []);

  const dismissNotification = useCallback(async (id) => {
    let prev;
    setNotifications((p) => { prev = p; return p.filter((n) => n.id !== id); });
    try { await call(store.deleteNotification, id); }
    catch (err) {
      console.warn('[AppContext] dismissNotification failed:', err?.message);
      if (prev) setNotifications(prev);
    }
  }, []);

  const registerPushDeviceToken = useCallback(async (tokenInfo) => (
    call(store.registerPushDeviceToken, tokenInfo)
  ), []);

  const listPushDeviceTokens = useCallback(async () => (
    call(store.listPushDeviceTokens)
  ), []);

  const retirePushDeviceToken = useCallback(async (target) => (
    call(store.retirePushDeviceToken, target)
  ), []);

  // ── CONTACTS ───────────────────────────────────────────────────────────────

  const addContact = useCallback(async (c) => {
    setContacts(prev => [c, ...prev]);
    try {
      // store.addContact returns the confirmed server row so the caller
      // can safely reference the id downstream (e.g. the deals insert
      // uses contact.id as an FK). Replace the optimistic entry with
      // the confirmed row when we get one.
      const confirmed = await call(store.addContact, c);
      if (confirmed && confirmed.id) {
        setContacts(prev => prev.map(x => (x.id === c.id || x.id === confirmed.id) ? confirmed : x));
      }
      // And RETURN it. This used to end here, returning undefined, so
      // every caller that checked the result read the write as failed —
      // Convert to Deal threw "the contact record did not save" on a
      // contact that had just been inserted successfully, and left the
      // orphan behind on every retry.
      //
      // The local store writes without returning a row, so fall back to
      // the record we sent: reaching here at all means the write did not
      // throw. A genuine failure rejects above and never returns.
      return confirmed?.id ? confirmed : c;
    } catch (err) {
      console.error('[AppContext] addContact failed:', err);
      setContacts(prev => prev.filter(x => x.id !== c.id));
      throw err;
    }
  }, []);

  const updateContact = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    let prevName = null;
    const patchKeys = Object.keys(updates || {});
    setContacts(p => {
      prev = p;
      prevRow = p.find((c) => c.id === id) || null;
      prevName = prevRow?.fullName || null;
      return p.map(c => c.id === id ? { ...c, ...updates } : c);
    });
    markLocalWrite('contacts', id);
    try {
      await call(store.updateContact, id, updates);
      // Fan-out: if the contact's display name changed, sync the
      // denormalized owner_name column on every deal that references
      // this contact. Without this the Deal Tracker card label and
      // Owner Dashboard header stay stale until the deal row is
      // re-saved for some other reason. Non-fatal on failure.
      const newName = Object.prototype.hasOwnProperty.call(updates, 'fullName')
        ? updates.fullName
        : null;
      if (newName != null && newName !== prevName) {
        try {
          // Read the current deals state to find which ones reference
          // this contact. Using the setter callback form is the only
          // way to peek at the latest value inside a useCallback whose
          // dep array doesn't include `deals`.
          let targets = [];
          setDeals((currentDeals) => {
            targets = (currentDeals || []).filter((d) => d.contactId === id && d.ownerName !== newName);
            return currentDeals;
          });
          await Promise.allSettled(
            targets.map((d) => call(store.updateDeal, d.id, { ownerName: newName }))
          );
          if (targets.length > 0) {
            // Reflect the change locally so kanban cards re-render
            // without waiting for the realtime channel echo.
            setDeals((cur) => (cur || []).map((d) => (
              d.contactId === id ? { ...d, ownerName: newName } : d
            )));
          }
        } catch (fanErr) {
          console.warn('[AppContext] updateContact fan-out to deals failed:', fanErr?.message);
        }
      }
    } catch (err) {
      console.error('[AppContext] updateContact failed:', err);
      if (prevRow) setContacts((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setContacts(prev);
      throw err;
    }
  }, [markLocalWrite]);

  const deleteContact = useCallback(async (id) => {
    let prev;
    setContacts(p => { prev = p; return p.filter(c => c.id !== id); });
    try {
      await call(store.deleteContact, id);
    } catch (err) {
      console.error('[AppContext] deleteContact failed:', err);
      if (prev) setContacts(prev);
      throw err;
    }
  }, []);

  // ── OWNERSHIP ──────────────────────────────────────────────────────────────

  const saveOwnership = useCallback(async (record) => {
    if (record?.dealId) requireDealEditor(record.dealId, 'edit ownership records');
    let prevRow;
    const patchKeys = Object.keys(record || {});
    setOwnership(p => {
      const idx = p.findIndex(o => o.id === record.id);
      prevRow = idx !== -1 ? p[idx] : null;
      return idx !== -1
        ? p.map(o => o.id === record.id ? record : o)
        : [...p, record];
    });
    if (record?.id) markLocalWrite('ownership_records', record.id);
    try {
      await call(store.saveOwnership, record);
    } catch (err) {
      console.error('[AppContext] saveOwnership failed:', err);
      if (prevRow) setOwnership((current) => rollbackOptimisticRowInList(current, record.id, prevRow, patchKeys));
      else setOwnership((current) => current.filter((o) => o.id !== record.id));
      throw err;
    }
  }, [markLocalWrite, requireDealEditor]);

  const deleteOwnership = useCallback(async (id) => {
    const currentRow = ownership.find((o) => o.id === id) || null;
    if (currentRow?.dealId) requireDealEditor(currentRow.dealId, 'delete ownership records');
    let prev;
    setOwnership(p => { prev = p; return p.filter(o => o.id !== id); });
    try {
      await call(store.deleteOwnership, id);
    } catch (err) {
      console.error('[AppContext] deleteOwnership failed:', err);
      if (prev) setOwnership(prev);
      throw err;
    }
  }, [ownership, requireDealEditor]);

  // Post-migration-019 lookup. Prefers the in-memory ownership cache so
  // callers avoid an extra round-trip during typical rendering, and
  // falls back to the store's DB helper only if the row hasn't been
  // loaded locally yet. Returns null when nothing matches so consumers
  // can render the empty-ownership state.
  const getOwnershipForEstimate = useCallback(
    (dealId, interestEstimateId) => {
      if (!dealId || !interestEstimateId) return null;
      return (
        ownership.find(
          (o) => o.dealId === dealId && o.interestEstimateId === interestEstimateId,
        ) ?? null
      );
    },
    [ownership],
  );

  // ── DEALS ──────────────────────────────────────────────────────────────────

  const addDeal = useCallback(async (d) => {
    const dealToSave = {
      ...prepareDealForInsert(stampCreatorAsAgent(user, d)),
      createdBy: d?.createdBy || user?.id || null,
    };
    if (canUserAccessDeal(user, dealToSave)) {
      dealsRef.current = [dealToSave, ...(dealsRef.current || []).filter((x) => x.id !== dealToSave.id)];
      setDeals(prev => [dealToSave, ...prev]);
    }
    try {
      // store.addDeal now returns the confirmed server row (via
      // `.select().single()`). When we get it back we replace the
      // optimistic entry with server truth so id / created_at /
      // default-column values stay in sync with the DB — otherwise a
      // later real-time UPDATE targeting the server-generated row
      // could accidentally create a second deal in state.
      let confirmed = await call(store.addDeal, dealToSave);
      if (confirmed?.id && dealToSave.stage === 'Preliminary Title' && confirmed.stage !== 'Preliminary Title') {
        confirmed = await call(store.updateDeal, confirmed.id, {
          stage: 'Preliminary Title',
          lifecycle: dealToSave.lifecycle,
        }) || { ...confirmed, stage: 'Preliminary Title', lifecycle: dealToSave.lifecycle };
      }
      if (confirmed && confirmed.id) {
        if (canUserAccessDeal(user, confirmed)) {
          dealsRef.current = (dealsRef.current || []).map((x) => (x.id === dealToSave.id || x.id === confirmed.id) ? confirmed : x);
          setDeals(prev => prev.map(x => (x.id === dealToSave.id || x.id === confirmed.id) ? confirmed : x));
        } else {
          dealsRef.current = (dealsRef.current || []).filter((x) => x.id !== dealToSave.id && x.id !== confirmed.id);
          setDeals(prev => prev.filter(x => x.id !== dealToSave.id && x.id !== confirmed.id));
        }
        // Preliminary Title is worked directly from the deal/map
        // document library now; no title-intake assignment email or
        // public intake link is created for new deals.
      }
      return confirmed || dealToSave;
    } catch (err) {
      dealsRef.current = (dealsRef.current || []).filter((x) => x.id !== dealToSave.id);
      setDeals(prev => prev.filter(x => x.id !== dealToSave.id));
      throw err;
    }
  }, [user]);

  // The deals list omits heavy JSONB columns (activities/notifications/
  // todos/attached_document_ids). OwnerDashboard calls this on mount to
  // hydrate the active deal with the full payload — fast for callers
  // that only care about a single record.
  const getDealFull = useCallback(async (id) => {
    if (!id) return null;
    if (!store.getDealFull) {
      // Older local stores without the helper just return whatever's in state.
      const d = await call(store.getDeals);
      const deal = Array.isArray(d) ? d.find(x => x.id === id) || null : null;
      return canUserAccessDeal(user, deal) ? deal : null;
    }
    const deal = await call(store.getDealFull, id);
    return canUserAccessDeal(user, deal) ? deal : null;
  }, [user]);

  const updateDeal = useCallback(async (id, updates, options = {}) => {
    const editableDeal = requireDealEditor(id);
    if (normalizeStage(updates?.stage) === 'Funded' && !isBradUser(user)) {
      throw new Error('Only Brad can approve a Closed deal and move it to Funded.');
    }
    let prev;
    let prevDealSnapshot;
    const patchKeys = Object.keys(updates || {});
    const optimisticUpdatedAt = new Date().toISOString();
    setDeals(p => {
      prev = p;
      prevDealSnapshot = p.find(d => d.id === id) || editableDeal;
      return p.flatMap((d) => {
        if (d.id !== id) return [d];
        const nextDeal = { ...d, ...updates, updatedAt: optimisticUpdatedAt };
        return canUserAccessDeal(user, nextDeal) ? [nextDeal] : [];
      });
    });
    // Field-level stamp: the realtime handler uses these field names to
    // preserve just-written fields while still applying concurrent edits
    // made by other users to other fields on the same deal.
    markLocalWrite('deals', id, Object.keys(updates || {}));
    let persistedDeal = null;
    try {
      persistedDeal = await call(store.updateDeal, id, updates, options);
    }
    catch (err) {
      if (prevDealSnapshot) {
        setDeals((current) => {
          const found = current.some((d) => d.id === id);
          if (!found) {
            return canUserAccessDeal(user, prevDealSnapshot)
              ? [prevDealSnapshot, ...current]
              : current;
          }
          return current.flatMap((d) => {
            if (d.id !== id) return [d];
            const rolledBack = rollbackOptimisticPatch(d, prevDealSnapshot, patchKeys, optimisticUpdatedAt);
            return canUserAccessDeal(user, rolledBack) ? [rolledBack] : [];
          });
        });
      } else if (prev) {
        setDeals(prev);
      }
      throw err;
    }
    if (persistedDeal) {
      setDeals((current) => {
        const nextDeal = { ...(prevDealSnapshot || {}), ...persistedDeal };
        const visible = canUserAccessDeal(user, nextDeal);
        const found = current.some((d) => d.id === id);
        if (!visible) return current.filter((d) => d.id !== id);
        if (!found) return [nextDeal, ...current];
        return current.map((d) => (d.id === id ? { ...d, ...persistedDeal } : d));
      });
    }

    // Full title is no longer ordered from here.
    //
    // It used to fire on the move into Pricing, and only when Mays moved
    // the deal. It is a stage policy now — Under Contract orders full
    // title, server-side, from the stage report notifyDealStageChange
    // already sends on every move, with the nightly sweep as the floor
    // under a lost report. See ensureFinalTitleOrdered in
    // api/title-services/send-deal.js for the reasoning, and for why it
    // never moves the deal on completion.

    // Year-end tax materialization. When a deal flips into Closed /
    // Funded (or arrives in that stage with new Estimator
    // data), persist the commission split + the royalty payment so
    // the 1099 / K-1 generators have a permanent record. Idempotent
    // — the unique indexes catch dupes.
    const stageJustClosed =
      updates?.stage
      && CLOSED_STAGES_SET.has(normalizeStage(updates.stage))
      && !CLOSED_STAGES_SET.has(normalizeStage(prevDealSnapshot?.stage));
    if (stageJustClosed) {
      // Run materialization out-of-band; failures shouldn't roll back
      // the stage change. Errors land in the console, and the gap is
      // visible and fixable afterwards: Accounting lists every closed
      // deal holding no payout rows and offers to write them
      // (selectUnmaterializedDeals). That matters because this only
      // fires on a stage CHANGE through updateDeal — a deal corrected
      // into Closed by any other route never reaches here at all.
      const dealNow = { ...(prevDealSnapshot || {}), ...updates };
      Promise.all([
        materializeCommissionPayouts(dealNow, {
          staffUsers: staffUsersRef.current,
          buyers: buyersRef.current,
          investors: investorsRef.current,
        }, store),
        materializeRoyaltyPayouts(dealNow, store),
      ]).catch((err) => {
        console.warn('[updateDeal] tax materialization failed:', err?.message || err);
      });
    }
    // Buyer changed on an ALREADY-closed deal (e.g. an outside company
    // swapped to Paraclete): rebuild the persisted split so the sell
    // agent's row is removed and the buy agent moves to the 50%
    // Paraclete rate — appending alone can't take rows away.
    // A placement change moves the sell-side 10% to the investor rep, so
    // it rebuilds the split for exactly the reason a buyer change does:
    // appending alone cannot take the old row away.
    //
    // dispositionManagerId belongs here too. Naming a manager on an
    // already-closed deal carves 5% out of the Operating Fund — two
    // rows change, one appears — and without this the persisted ledger
    // kept the pre-assignment split while the live Accounting page,
    // which recomputes per deal, showed the new one. The two then
    // disagreed about the same closed deal.
    const buyerFieldsTouched = updates && (
      'dispositionBuyerId' in updates || 'interestEstimates' in updates
      || 'investorId' in updates || 'investorEntityId' in updates
      || 'dispositionManagerId' in updates
    );
    const alreadyClosed = !stageJustClosed
      && CLOSED_STAGES_SET.has(normalizeStage(updates?.stage || prevDealSnapshot?.stage));
    if (alreadyClosed && buyerFieldsTouched) {
      const dealNow = { ...(prevDealSnapshot || {}), ...updates };
      rematerializeCommissionPayouts(dealNow, {
        staffUsers: staffUsersRef.current,
        buyers: buyersRef.current,
        investors: investorsRef.current,
      }, store).catch((err) => {
        console.warn('[updateDeal] commission rebuild failed:', err?.message || err);
      });
    }
  }, [markLocalWrite, requireDealEditor, user]);

  const deleteDeal = useCallback(async (id) => {
    if (user?.role !== 'admin') throw new Error('Only admins can delete deals.');
    requireDealEditor(id, 'delete this deal');
    let prev;
    setDeals(p => { prev = p; return p.filter(d => d.id !== id); });
    try { await call(store.deleteDeal, id); }
    catch (err) { if (prev) setDeals(prev); throw err; }
  }, [requireDealEditor, user?.role]);

  // ── Normalized deal-children writes (migration-028) ────────────────
  // Each one is a single SQL statement against the dedicated table.
  // Optimistic local update first; rollback on failure so the UI
  // doesn't pretend a write succeeded when it didn't.

  const addDealTodo = useCallback(async (entry) => {
    if (!entry?.dealId || !entry?.id) throw new Error('addDealTodo: dealId + id required');
    requireDealEditor(entry.dealId, 'add to-dos');
    setDealTodos(prev => prev.some(t => t.id === entry.id) ? prev : [entry, ...prev]);
    try {
      const confirmed = await call(store.addDealTodo, entry);
      if (confirmed) setDealTodos(prev => prev.map(t => t.id === entry.id ? confirmed : t));
      return confirmed;
    } catch (err) {
      setDealTodos(prev => prev.filter(t => t.id !== entry.id));
      throw err;
    }
  }, [requireDealEditor]);

  const updateDealTodo = useCallback(async (id, patch) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(patch || {});
    const currentRow = dealTodos.find((t) => t.id === id) || null;
    if (!currentRow?.dealId) throw new Error('Todo not found.');
    requireDealEditor(currentRow.dealId, 'edit to-dos');
    setDealTodos(p => {
      prev = p;
      prevRow = p.find((t) => t.id === id) || null;
      return p.map(t => t.id === id ? { ...t, ...patch } : t);
    });
    try { await call(store.updateDealTodo, id, patch); }
    catch (err) {
      if (prevRow) setDealTodos((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setDealTodos(prev);
      throw err;
    }
  }, [dealTodos, requireDealEditor]);

  const deleteDealTodo = useCallback(async (id) => {
    const currentRow = dealTodos.find((t) => t.id === id) || null;
    if (!currentRow?.dealId) throw new Error('Todo not found.');
    requireDealEditor(currentRow.dealId, 'delete to-dos');
    let prev;
    setDealTodos(p => { prev = p; return p.filter(t => t.id !== id); });
    try { await call(store.deleteDealTodo, id); }
    catch (err) { if (prev) setDealTodos(prev); throw err; }
  }, [dealTodos, requireDealEditor]);

  const addDealActivity = useCallback(async (entry) => {
    if (!entry?.dealId || !entry?.id) throw new Error('addDealActivity: dealId + id required');
    requireDealEditor(entry.dealId, 'add activity');
    setDealActivities(prev => prev.some(a => a.id === entry.id) ? prev : [entry, ...prev]);
    // The stamp is what the board reads, so it moves with the row or the
    // card goes on claiming the deal has been quiet. Captured first so a
    // failed write puts it back: a stamp that is too FRESH silences a
    // staleness warning that should have fired.
    const previousStamp = dealActivityStamps.get(entry.dealId);
    const at = entry.createdAt || new Date().toISOString();
    setDealActivityStamps(prev => {
      if ((prev.get(entry.dealId) || '') >= at) return prev;
      const next = new Map(prev);
      next.set(entry.dealId, at);
      return next;
    });
    try {
      const confirmed = await call(store.addDealActivity, entry);
      if (confirmed) setDealActivities(prev => prev.map(a => a.id === entry.id ? confirmed : a));
      return confirmed;
    } catch (err) {
      setDealActivities(prev => prev.filter(a => a.id !== entry.id));
      setDealActivityStamps(prev => {
        if (prev.get(entry.dealId) !== at) return prev;
        const next = new Map(prev);
        if (previousStamp) next.set(entry.dealId, previousStamp);
        else next.delete(entry.dealId);
        return next;
      });
      throw err;
    }
  }, [requireDealEditor, dealActivityStamps]);

  // Deleting the newest activity on a deal leaves its stamp a little too
  // fresh until the next page load, which can only hide a staleness
  // warning for the rest of a session. Recomputing it would mean asking
  // the database for the new newest row on every delete, and deleting an
  // activity is rare enough that the trade is worth stating rather than
  // paying for.
  const deleteDealActivity = useCallback(async (id) => {
    const currentRow = dealActivities.find((a) => a.id === id) || null;
    if (!currentRow?.dealId) throw new Error('Activity not found.');
    requireDealEditor(currentRow.dealId, 'delete activity');
    let prev;
    setDealActivities(p => { prev = p; return p.filter(a => a.id !== id); });
    try { await call(store.deleteDealActivity, id); }
    catch (err) { if (prev) setDealActivities(prev); throw err; }
  }, [dealActivities, requireDealEditor]);

  const upsertDealSignatureRequest = useCallback(async (entry) => {
    if (!entry?.dealId || !entry?.id) throw new Error('upsertDealSignatureRequest: dealId + id required');
    requireDealEditor(entry.dealId, 'edit signature requests');
    let prevRow;
    const patchKeys = Object.keys(entry || {});
    setDealSignatureRequests(p => {
      prevRow = p.find((s) => s.id === entry.id) || null;
      const exists = !!prevRow;
      if (exists) return p.map(s => s.id === entry.id ? { ...s, ...entry } : s);
      return [entry, ...p];
    });
    try {
      const confirmed = await call(store.upsertDealSignatureRequest, entry);
      if (confirmed) setDealSignatureRequests(p => p.map(s => s.id === entry.id ? confirmed : s));
      return confirmed;
    } catch (err) {
      if (prevRow) setDealSignatureRequests((current) => rollbackOptimisticRowInList(current, entry.id, prevRow, patchKeys));
      else setDealSignatureRequests((current) => current.filter((s) => s.id !== entry.id));
      throw err;
    }
  }, [requireDealEditor]);

  const deleteDealSignatureRequest = useCallback(async (id) => {
    const currentRow = dealSignatureRequests.find((s) => s.id === id) || null;
    if (!currentRow?.dealId) throw new Error('Signature request not found.');
    requireDealEditor(currentRow.dealId, 'delete signature requests');
    let prev;
    setDealSignatureRequests(p => { prev = p; return p.filter(s => s.id !== id); });
    try { await call(store.deleteDealSignatureRequest, id); }
    catch (err) { if (prev) setDealSignatureRequests(prev); throw err; }
  }, [dealSignatureRequests, requireDealEditor]);

  /**
   * Taking an unclaimed deal.
   *
   * No requireDealEditor: the whole point is that the claimer is NOT
   * on the deal yet. The database decides, and it decides on one
   * condition — buy_side_agent_id is null — inside a single statement,
   * so two managers clicking the same card resolve to one winner and
   * the other is told so rather than silently overwriting them.
   */
  const claimDeal = useCallback(async (dealId) => {
    if (!dealId) throw new Error('claimDeal: dealId required');
    const claimed = await call(store.claimDeal, dealId);
    // Merged rather than refetched: the board is already holding this
    // deal and the claim changes one field on it.
    if (claimed?.id) {
      setDeals((prev) => prev.map((d) => (d.id === claimed.id ? { ...d, ...claimed } : d)));
    }
    return claimed;
  }, []);

  const createCertifiedMailJob = useCallback(async (entry) => {
    // Read at call time from the status the server reported, not from a
    // build-time copy of an environment variable. The server refuses
    // anyway; this is here so the refusal is instant and worded the same.
    if (physicalMailingComingSoon()) {
      throw new Error('Physical mailing tools are temporarily disabled. Coming soon.');
    }
    if (!entry?.dealId || !entry?.id) throw new Error('createCertifiedMailJob: dealId + id required');
    requireDealEditor(entry.dealId, 'create certified mail jobs');
    const optimistic = {
      ...entry,
      status: entry.status || 'queued',
      provider: entry.provider || 'manual',
      createdAt: entry.createdAt || new Date().toISOString(),
      updatedAt: entry.updatedAt || entry.createdAt || new Date().toISOString(),
    };
    setCertifiedMailJobs(prev => prev.some(m => m.id === optimistic.id) ? prev : [optimistic, ...prev]);
    try {
      const response = await authedFetch('/api/certified-mail/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || `Certified mail request failed (${response.status})`);
      const confirmed = body?.job || optimistic;
      setCertifiedMailJobs(prev => prev.map(m => m.id === optimistic.id ? confirmed : m));
      return confirmed;
    } catch (err) {
      setCertifiedMailJobs(prev => prev.filter(m => m.id !== optimistic.id));
      throw err;
    }
  }, [requireDealEditor]);

  // ── Deals + children merge memo ───────────────────────────────────
  // Attach the normalized child rows so existing readers (deal.todos /
  // deal.activities / deal.signatureRequests / deal.certifiedMailJobs)
  // keep working unchanged.
  //
  // Incremental, and that is the point rather than a refinement: the
  // merge used to spread EVERY deal into a new object whenever any of
  // the four arrays changed, and a new deal object re-renders every
  // memoised card on the Deal Tracker and re-runs the estimator
  // rollups, deal health and the approval queue over the whole board.
  // The four arrays land separately at start-up and an activity row is
  // written on every stage change, so that was happening constantly,
  // for every person watching. See src/utils/attachDealChildren.js.
  const attachChildren = useRef(createDealChildrenAttacher()).current;
  const dealsWithChildren = useMemo(
    () => attachChildren(deals, {
      todos: dealTodos,
      activities: dealActivities,
      signatureRequests: dealSignatureRequests,
      certifiedMailJobs,
    }, dealActivityStamps),
    [attachChildren, deals, dealTodos, dealActivities, dealSignatureRequests, certifiedMailJobs, dealActivityStamps],
  );

  // ── OFFERS ────────────────────────────────────────────────────────────────

  const addOffer = useCallback(async (o) => {
    if (o?.dealId) requireDealEditor(o.dealId, 'add offers');
    setOffers(prev => [o, ...prev]);
    try {
      const confirmed = await call(store.addOffer, o);
      if (confirmed && confirmed.id) {
        setOffers(prev => prev.map(x => (x.id === o.id || x.id === confirmed.id) ? confirmed : x));
      }
    } catch (err) {
      setOffers(prev => prev.filter(x => x.id !== o.id));
      throw err;
    }
  }, [requireDealEditor]);

  const updateOffer = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    const currentOffer = offers.find((o) => o.id === id) || null;
    if (currentOffer?.dealId) requireDealEditor(currentOffer.dealId, 'edit offers');
    setOffers(p => {
      prev = p;
      prevRow = p.find((o) => o.id === id) || null;
      return p.map(o => o.id === id ? { ...o, ...updates } : o);
    });
    try { await call(store.updateOffer, id, updates); }
    catch (err) {
      if (prevRow) setOffers((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setOffers(prev);
      throw err;
    }
  }, [offers, requireDealEditor]);

  const deleteOffer = useCallback(async (id) => {
    const currentOffer = offers.find((o) => o.id === id) || null;
    if (currentOffer?.dealId) requireDealEditor(currentOffer.dealId, 'delete offers');
    let prev;
    setOffers(p => { prev = p; return p.filter(o => o.id !== id); });
    try { await call(store.deleteOffer, id); }
    catch (err) { if (prev) setOffers(prev); throw err; }
  }, [offers, requireDealEditor]);

  // ── BUYERS ────────────────────────────────────────────────────────────────

  const addBuyer = useCallback(async (b) => {
    setBuyers(prev => [b, ...prev]);
    // Hard timeout so a hung Supabase write doesn't leave the modal
    // stuck on "Saving…" forever. 10s per attempt × one retry on
    // transient errors = ~20s worst case before the error banner
    // appears; shorter than 30s so users don't give up first.
    const withTimeout = (p, ms) => Promise.race([
      p,
      new Promise((_, reject) => setTimeout(
        () => reject(new Error(`Save timeout after ${ms / 1000}s`)),
        ms,
      )),
    ]);
    try {
      const confirmed = await withTimeout(call(store.addBuyer, b), 10000);
      if (confirmed && confirmed.id) {
        setBuyers(prev => prev.map(x => (x.id === b.id || x.id === confirmed.id) ? confirmed : x));
      }
    } catch (err) {
      // One retry on transient failures — Supabase occasionally wobbles
      // on network or auth-token refresh and the second attempt goes
      // through cleanly. Also retry on our own timeout error so a slow
      // first request doesn't immediately surface as a user-visible
      // failure.
      let finalErr = err;
      const isTransient = /Failed to fetch|timeout|timed out|NetworkError/i.test(err?.message || '');
      if (isTransient) {
        try {
          const confirmed = await withTimeout(call(store.addBuyer, b), 10000);
          if (confirmed && confirmed.id) {
            setBuyers(prev => prev.map(x => (x.id === b.id || x.id === confirmed.id) ? confirmed : x));
          }
          return;
        } catch (retryErr) {
          finalErr = retryErr;
        }
      }
      console.error('[AppContext] addBuyer failed:', finalErr);
      setBuyers(prev => prev.filter(x => x.id !== b.id));
      setStorageError({
        title: 'Buyer save failed',
        message: finalErr?.message
          ? `Could not save the buyer: ${finalErr.message}. The entry was not persisted — please try again.`
          : 'Could not save the buyer — please try again.',
      });
      throw finalErr;
    }
  }, []);

  // One optimistic writer for both investor tables, rather than four
  // more copies of the buyer dance above. Same semantics — a 10s cap so
  // a hung write surfaces instead of spinning, and one retry on the
  // transient failures Supabase produces around token refresh.
  const investorWrite = useCallback(async (setList, run, optimistic, rollback, label) => {
    if (optimistic) setList(optimistic);
    const withTimeout = (p2, ms) => Promise.race([
      p2,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`Save timeout after ${ms / 1000}s`)), ms)),
    ]);
    try {
      return await withTimeout(run(), 10000);
    } catch (err) {
      const transient = /Failed to fetch|timeout|timed out|NetworkError/i.test(err?.message || '');
      if (transient) {
        try { return await withTimeout(run(), 10000); } catch { /* fall through to the rollback */ }
      }
      console.error(`[AppContext] ${label} failed:`, err);
      if (rollback) setList(rollback);
      setStorageError({
        title: `${label} failed`,
        message: err?.message
          ? `Could not save: ${err.message}. Nothing was persisted — please try again.`
          : 'Could not save — please try again.',
      });
      throw err;
    }
  }, []);

  const addInvestor = useCallback(async (investor) => {
    const confirmed = await investorWrite(
      setInvestors,
      () => call(store.addInvestor, investor),
      (prev) => [investor, ...prev],
      (prev) => prev.filter((x) => x.id !== investor.id),
      'Investor save',
    );
    if (confirmed?.id) {
      setInvestors((prev) => prev.map((x) => (x.id === investor.id || x.id === confirmed.id) ? confirmed : x));
    }
  }, [investorWrite]);

  const updateInvestor = useCallback(async (id, updates) => {
    let before = null;
    setInvestors((prev) => { before = prev; return prev.map((x) => x.id === id ? { ...x, ...updates } : x); });
    await investorWrite(setInvestors, () => call(store.updateInvestor, id, updates), null, before, 'Investor update');
  }, [investorWrite]);

  const deleteInvestor = useCallback(async (id) => {
    let before = null;
    setInvestors((prev) => { before = prev; return prev.filter((x) => x.id !== id); });
    // The database refuses this while any entity still points at the
    // investor, so the rollback is the common path rather than the rare
    // one — an admin will hit it whenever they forget the entities.
    await investorWrite(setInvestors, () => call(store.deleteInvestor, id), null, before, 'Investor delete');
  }, [investorWrite]);

  const addInvestorEntity = useCallback(async (entity) => {
    const confirmed = await investorWrite(
      setInvestorEntities,
      () => call(store.addInvestorEntity, entity),
      (prev) => [entity, ...prev],
      (prev) => prev.filter((x) => x.id !== entity.id),
      'Entity save',
    );
    if (confirmed?.id) {
      setInvestorEntities((prev) => prev.map((x) => (x.id === entity.id || x.id === confirmed.id) ? confirmed : x));
    }
  }, [investorWrite]);

  const updateInvestorEntity = useCallback(async (id, updates) => {
    let before = null;
    setInvestorEntities((prev) => { before = prev; return prev.map((x) => x.id === id ? { ...x, ...updates } : x); });
    await investorWrite(setInvestorEntities, () => call(store.updateInvestorEntity, id, updates), null, before, 'Entity update');
  }, [investorWrite]);

  const deleteInvestorEntity = useCallback(async (id) => {
    let before = null;
    setInvestorEntities((prev) => { before = prev; return prev.filter((x) => x.id !== id); });
    // Refused while a deal names this entity — the deed was generated
    // from it, so it must outlive any tidying up.
    await investorWrite(setInvestorEntities, () => call(store.deleteInvestorEntity, id), null, before, 'Entity delete');
  }, [investorWrite]);

  // Asset income — royalty cheques, lease bonuses, shut-in and delay
  // rentals. The same optimistic-write-then-reconcile shape as the
  // investor rows, through the same helper, so a failed insert rolls
  // the list back instead of leaving a receipt on screen that is not
  // in the database.
  const addAssetIncomeRow = useCallback(async (row) => {
    const confirmed = await investorWrite(
      setAssetIncome,
      () => call(store.addAssetIncome, row),
      (prev) => [row, ...prev],
      (prev) => prev.filter((x) => x.id !== row.id),
      'Income save',
    );
    if (confirmed?.id) {
      setAssetIncome((prev) => prev.map((x) => (x.id === row.id || x.id === confirmed.id) ? confirmed : x));
    }
  }, [investorWrite]);

  const updateAssetIncomeRow = useCallback(async (id, updates) => {
    let before = null;
    setAssetIncome((prev) => { before = prev; return prev.map((x) => x.id === id ? { ...x, ...updates } : x); });
    await investorWrite(setAssetIncome, () => call(store.updateAssetIncome, id, updates), null, before, 'Income update');
  }, [investorWrite]);

  const deleteAssetIncomeRow = useCallback(async (id) => {
    let before = null;
    setAssetIncome((prev) => { before = prev; return prev.filter((x) => x.id !== id); });
    await investorWrite(setAssetIncome, () => call(store.deleteAssetIncome, id), null, before, 'Income delete');
  }, [investorWrite]);

  const updateBuyer = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    setBuyers(p => {
      prev = p;
      prevRow = p.find((b) => b.id === id) || null;
      return p.map(b => b.id === id ? { ...b, ...updates } : b);
    });
    // Same 10s cap as addBuyer so edits surface an error instead of
    // spinning forever when the network or Supabase stalls.
    const withTimeout = (p2, ms) => Promise.race([
      p2,
      new Promise((_, reject) => setTimeout(
        () => reject(new Error(`Save timeout after ${ms / 1000}s`)),
        ms,
      )),
    ]);
    try { await withTimeout(call(store.updateBuyer, id, updates), 10000); }
    catch (err) {
      if (prevRow) setBuyers((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setBuyers(prev);
      throw err;
    }
  }, []);

  const deleteBuyer = useCallback(async (id) => {
    let prev;
    setBuyers(p => { prev = p; return p.filter(b => b.id !== id); });
    try { await call(store.deleteBuyer, id); }
    catch (err) { if (prev) setBuyers(prev); throw err; }
  }, []);

  // ── PACKAGES ──────────────────────────────────────────────────────────────

  const addPackage = useCallback(async (p) => {
    assertEditableDealIds(p?.dealIds || [], 'create deal packages');
    setPackages(prev => [p, ...prev]);
    try {
      const confirmed = await call(store.addPackage, p);
      if (confirmed && confirmed.id) {
        setPackages(prev => prev.map(x => (x.id === p.id || x.id === confirmed.id) ? confirmed : x));
      }
    } catch (err) {
      setPackages(prev => prev.filter(x => x.id !== p.id));
      throw err;
    }
  }, [assertEditableDealIds]);

  const updatePackage = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    const currentPackage = packages.find((p) => p.id === id) || null;
    assertEditableDealIds([...(currentPackage?.dealIds || []), ...(updates?.dealIds || [])], 'edit deal packages');
    setPackages(p => {
      prev = p;
      prevRow = p.find((x) => x.id === id) || null;
      return p.map(x => x.id === id ? { ...x, ...updates } : x);
    });
    try { await call(store.updatePackage, id, updates); }
    catch (err) {
      if (prevRow) setPackages((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setPackages(prev);
      throw err;
    }
  }, [assertEditableDealIds, packages]);

  const deletePackage = useCallback(async (id) => {
    const currentPackage = packages.find((p) => p.id === id) || null;
    assertEditableDealIds(currentPackage?.dealIds || [], 'delete deal packages');
    let prev;
    setPackages(p => { prev = p; return p.filter(x => x.id !== id); });
    try { await call(store.deletePackage, id); }
    catch (err) { if (prev) setPackages(prev); throw err; }
  }, [assertEditableDealIds, packages]);

  // ── CLOSINGS ──────────────────────────────────────────────────────────────

  const addClosing = useCallback(async (c) => {
    if (c?.dealId) requireDealEditor(c.dealId, 'add closings');
    setClosings(prev => [c, ...prev]);
    try {
      const confirmed = await call(store.addClosing, c);
      if (confirmed && confirmed.id) {
        setClosings(prev => prev.map(x => (x.id === c.id || x.id === confirmed.id) ? confirmed : x));
      }
    } catch (err) {
      setClosings(prev => prev.filter(x => x.id !== c.id));
      throw err;
    }
  }, [requireDealEditor]);

  const updateClosing = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    const currentClosing = closings.find((c) => c.id === id) || null;
    if (currentClosing?.dealId) requireDealEditor(currentClosing.dealId, 'edit closings');
    setClosings(p => {
      prev = p;
      prevRow = p.find((c) => c.id === id) || null;
      return p.map(c => c.id === id ? { ...c, ...updates } : c);
    });
    try { await call(store.updateClosing, id, updates); }
    catch (err) {
      if (prevRow) setClosings((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setClosings(prev);
      throw err;
    }
  }, [closings, requireDealEditor]);

  const deleteClosing = useCallback(async (id) => {
    const currentClosing = closings.find((c) => c.id === id) || null;
    if (currentClosing?.dealId) requireDealEditor(currentClosing.dealId, 'delete closings');
    let prev;
    setClosings(p => { prev = p; return p.filter(c => c.id !== id); });
    try { await call(store.deleteClosing, id); }
    catch (err) { if (prev) setClosings(prev); throw err; }
  }, [closings, requireDealEditor]);

  // ── CLOSING TASKS ─────────────────────────────────────────────────────────

  const addClosingTask = useCallback(async (t) => {
    const parentClosing = closings.find((c) => c.id === t?.closingId) || null;
    if (parentClosing?.dealId) requireDealEditor(parentClosing.dealId, 'add closing tasks');
    setClosingTasks(prev => [...prev, t]);
    try {
      const confirmed = await call(store.addClosingTask, t);
      if (confirmed && confirmed.id) {
        setClosingTasks(prev => prev.map(x => (x.id === t.id || x.id === confirmed.id) ? confirmed : x));
      }
    } catch (err) {
      setClosingTasks(prev => prev.filter(x => x.id !== t.id));
      throw err;
    }
  }, [closings, requireDealEditor]);

  const updateClosingTask = useCallback(async (id, updates) => {
    let prev;
    let prevRow;
    const patchKeys = Object.keys(updates || {});
    const currentTask = closingTasks.find((t) => t.id === id) || null;
    const parentClosing = closings.find((c) => c.id === currentTask?.closingId) || null;
    if (parentClosing?.dealId) requireDealEditor(parentClosing.dealId, 'edit closing tasks');
    setClosingTasks(p => {
      prev = p;
      prevRow = p.find((t) => t.id === id) || null;
      return p.map(t => t.id === id ? { ...t, ...updates } : t);
    });
    try { await call(store.updateClosingTask, id, updates); }
    catch (err) {
      if (prevRow) setClosingTasks((current) => rollbackOptimisticRowInList(current, id, prevRow, patchKeys));
      else if (prev) setClosingTasks(prev);
      throw err;
    }
  }, [closingTasks, closings, requireDealEditor]);

  const deleteClosingTask = useCallback(async (id) => {
    const currentTask = closingTasks.find((t) => t.id === id) || null;
    const parentClosing = closings.find((c) => c.id === currentTask?.closingId) || null;
    if (parentClosing?.dealId) requireDealEditor(parentClosing.dealId, 'delete closing tasks');
    let prev;
    setClosingTasks(p => { prev = p; return p.filter(t => t.id !== id); });
    try { await call(store.deleteClosingTask, id); }
    catch (err) { if (prev) setClosingTasks(prev); throw err; }
  }, [closingTasks, closings, requireDealEditor]);

  // ── STR PRICING ────────────────────────────────────────────────────────────

  const upsertPricing = useCallback(async (entry) => {
    // Optimistic: update in-memory map immediately
    const { updateSTRPricing: updateLocal } = await import('../data/strPricing');
    updateLocal(entry.section, entry.township, entry.range, entry.price, entry.tract);
    try {
      if (store.upsertSTRPricing) await call(store.upsertSTRPricing, entry);
    } catch (err) {
      console.error('[AppContext] upsertPricing failed:', err);
      // Reload from DB to revert optimistic update
      if (store.getSTRPricingData) {
        const rows = await call(store.getSTRPricingData);
        if (rows) { setStrPricing(rows); loadPricingFromDB(rows); }
      }
      throw err;
    }
  }, []);

  const deletePricing = useCallback(async (id) => {
    try {
      if (store.deleteSTRPricing) await call(store.deleteSTRPricing, id);
      // Reload after delete
      if (store.getSTRPricingData) {
        const rows = await call(store.getSTRPricingData);
        if (rows) { setStrPricing(rows); loadPricingFromDB(rows); }
      }
    } catch (err) {
      console.error('[AppContext] deletePricing failed:', err);
      throw err;
    }
  }, []);

  // ── Context value ──────────────────────────────────────────────────────────
  // The provider value is memoized. Without this, every state change
  // anywhere in the context (a single deal update, a notification
  // arriving, etc.) would build a fresh `{ ... }` object literal,
  // which compares by reference — every consumer of useApp() would
  // re-render even if nothing it actually reads changed. With the
  // memo, consumers only re-render when something they read changes.
  // The dep list contains every state field + every callback so the
  // value identity is stable across unrelated renders of this
  // provider.
  const documentIndex = useMemo(() => indexDocuments(documents), [documents]);
  const value = useMemo(() => ({
    // State — `deals` is the merged view (with todos / activities /
    // signatureRequests attached from the dedicated tables) so
    // existing readers don't change.
    assignments, flowcharts, documents, contacts, ownership, staffUsers,
    // `documents`, indexed by id and by deal. Built here, once per
    // documents identity, rather than in each reader: the Deal Tracker's
    // health cache is scoped on it, and a per-page index would be a new
    // object on every mount — which is precisely the cache miss this
    // exists to end. See src/utils/documentIndex.js.
    documentIndex,
    followUps, refreshFollowUps: loadFollowUps,
    dealStateSummaries,
    deals: dealsWithChildren,
    offers, buyers, packages, closings, closingTasks, strPricing, nmPricingReferences,
    investors, investorEntities,
    addInvestor, updateInvestor, deleteInvestor,
    addInvestorEntity, updateInvestorEntity, deleteInvestorEntity,
    assetIncome, addAssetIncomeRow, updateAssetIncomeRow, deleteAssetIncomeRow,
    notifications, markNotificationRead, markAllNotificationsRead, dismissNotification,
    registerPushDeviceToken, listPushDeviceTokens, retirePushDeviceToken,
    loading, secondaryLoading, allDocumentsLoaded,
    storageError, clearStorageError,

    // Actions
    createFlowchart, updateFlowchart, deleteFlowchart,
    addDocument, updateDocument, deleteDocument,
    addContact, updateContact, deleteContact,
    saveOwnership, deleteOwnership, getOwnershipForEstimate,
    addDeal, updateDeal, deleteDeal, getDealFull,
    // Normalized child writes (migration-028)
    addDealTodo, updateDealTodo, deleteDealTodo,
    addDealActivity, deleteDealActivity,
    upsertDealSignatureRequest, deleteDealSignatureRequest,
    claimDeal,
    createCertifiedMailJob,
    addOffer, updateOffer, deleteOffer,
    addBuyer, updateBuyer, deleteBuyer,
    addPackage, updatePackage, deletePackage,
    addClosing, updateClosing, deleteClosing,
    addClosingTask, updateClosingTask, deleteClosingTask,
    upsertPricing, deletePricing,
    loadAllDocuments, ensureDealDocuments, ensureDealActivities,
    ensureDealSignatureRequests, refresh,
  }), [
    assignments, flowcharts, documents, contacts, ownership, staffUsers,
    dealsWithChildren,
    offers, buyers, packages, closings, closingTasks, strPricing, nmPricingReferences,
    investors, investorEntities,
    addInvestor, updateInvestor, deleteInvestor,
    addInvestorEntity, updateInvestorEntity, deleteInvestorEntity,
    assetIncome, addAssetIncomeRow, updateAssetIncomeRow, deleteAssetIncomeRow,
    notifications, markNotificationRead, markAllNotificationsRead, dismissNotification,
    registerPushDeviceToken, listPushDeviceTokens, retirePushDeviceToken,
    followUps, loadFollowUps,
    dealStateSummaries,
    documentIndex,
    loading, secondaryLoading, allDocumentsLoaded,
    storageError, clearStorageError,
    createFlowchart, updateFlowchart, deleteFlowchart,
    addDocument, updateDocument, deleteDocument,
    addContact, updateContact, deleteContact,
    saveOwnership, deleteOwnership, getOwnershipForEstimate,
    addDeal, updateDeal, deleteDeal, getDealFull,
    addDealTodo, updateDealTodo, deleteDealTodo,
    addDealActivity, deleteDealActivity,
    upsertDealSignatureRequest, deleteDealSignatureRequest,
    claimDeal,
    createCertifiedMailJob,
    addOffer, updateOffer, deleteOffer,
    addBuyer, updateBuyer, deleteBuyer,
    addPackage, updatePackage, deletePackage,
    addClosing, updateClosing, deleteClosing,
    addClosingTask, updateClosingTask, deleteClosingTask,
    upsertPricing, deletePricing,
    loadAllDocuments, ensureDealDocuments, ensureDealActivities,
    ensureDealSignatureRequests, refresh,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
